export type ProductQueryMode = 'simple' | 'composite';

export type CompositeFilterType = 'category_of_interest' | 'claim_of_interest';

export type CompositeGroupRecord = {
  id: string;
  value?: Record<string, unknown>;
};

export type CompositeGroupsState = Record<CompositeFilterType, CompositeGroupRecord[]>;

/** Callback metadata only; keep it separate from business filters and request payloads. */
export type ModuleFilterChange = {
  source: 'user' | 'portfolio' | 'system_scope';
  options?: Partial<Record<
    'client_category_ids' | 'client_group_ids' | 'client_product_ids' | 'claim_interest_ids',
    ReadonlyArray<{ value: string | number; label: string }>
  >>;
};

/** Retain loaded primitive labels, never React nodes, entities, or invented ID labels. */
export const loadedModuleFilterOptions = (
  items: ReadonlyArray<{ value: unknown; label: unknown }>,
): ReadonlyArray<{ value: string | number; label: string }> => {
  const options = new Map<string, { value: string | number; label: string }>();
  for (const { value, label } of items) {
    if (
      !((typeof value === 'number' && Number.isSafeInteger(value) && value > 0) ||
        (typeof value === 'string' && value.trim().length > 0)) ||
      typeof label !== 'string' || !label.trim()
    ) continue;
    if (!options.has(String(value))) options.set(String(value), { value, label });
  }
  return [...options.values()];
};

export type ModuleQueryController = {
  queryMode: ProductQueryMode;
  moduleFilters: Record<string, any>;
  compositeGroups: CompositeGroupsState;
  enabledFilterTypes: CompositeFilterType[];
  analyticsSemantic?: boolean;
  setQueryMode: (mode: ProductQueryMode, change?: ModuleFilterChange) => void;
  updateModuleFilter: (
    key: CompositeFilterType,
    value?: Record<string, unknown>,
    change?: ModuleFilterChange,
  ) => void;
  updateCompositeGroup: (
    type: CompositeFilterType,
    id: string,
    value?: Record<string, unknown>,
    change?: ModuleFilterChange,
  ) => void;
  addCompositeGroup: (type: CompositeFilterType, change?: ModuleFilterChange) => void;
  removeCompositeGroup: (
    type: CompositeFilterType,
    id: string,
    change?: ModuleFilterChange,
  ) => void;
};

export const MAX_COMPOSITE_GROUPS_PER_TYPE = 10;

export const createEmptyCompositeGroups = (): CompositeGroupsState => ({
  category_of_interest: [],
  claim_of_interest: [],
});

export const isCompositeGroupConfigured = (
  type: CompositeFilterType,
  value?: Record<string, unknown>,
) => {
  if (!value) return false;
  if (type === 'category_of_interest') {
    if (value.mode === 'blends') {
      return Array.isArray(value.blend_ids) && value.blend_ids.length > 0;
    }
    const categoryIds = Array.isArray(value.category_ids) ? value.category_ids : [];
    if (categoryIds.length === 0) return false;
    return (
      value.mode !== 'full_custom' ||
      (Array.isArray(value.client_product_ids) && value.client_product_ids.length > 0) ||
      (Array.isArray(value.category_group_ids) && value.category_group_ids.length > 0)
    );
  }
  return Array.isArray(value.claim_interest_ids) && value.claim_interest_ids.length > 0;
};

export const hasInvalidCompositeGroups = (groups: CompositeGroupsState) =>
  groups.category_of_interest.some(
    (group) => !isCompositeGroupConfigured('category_of_interest', group.value),
  ) ||
  groups.claim_of_interest
    .slice(1)
    .some((group) => !isCompositeGroupConfigured('claim_of_interest', group.value));

export const hasConfiguredCompositeGroups = (groups: CompositeGroupsState) =>
  (Object.keys(groups) as CompositeFilterType[]).some((type) =>
    groups[type].some((group) => isCompositeGroupConfigured(type, group.value)),
  );

export const isModuleQueryReady = (
  queryMode: ProductQueryMode,
  enabledFilterTypes: CompositeFilterType[],
  simpleModuleFilters: Record<string, any>,
  compositeGroups: CompositeGroupsState,
) => {
  if (queryMode === 'simple') {
    return (
      !enabledFilterTypes.includes('category_of_interest') ||
      isCompositeGroupConfigured('category_of_interest', simpleModuleFilters.category_of_interest)
    );
  }

  if (enabledFilterTypes.length === 0 || hasInvalidCompositeGroups(compositeGroups)) {
    return false;
  }
  if (
    enabledFilterTypes.includes('category_of_interest') &&
    !compositeGroups.category_of_interest.some((group) =>
      isCompositeGroupConfigured('category_of_interest', group.value),
    )
  ) {
    return false;
  }
  return hasConfiguredCompositeGroups(compositeGroups);
};

export const buildActiveModuleFilters = (
  queryMode: ProductQueryMode,
  simpleModuleFilters: Record<string, unknown> = {},
  compositeGroups: CompositeGroupsState = createEmptyCompositeGroups(),
) => {
  if (queryMode !== 'composite') return simpleModuleFilters;

  const groups = (Object.keys(compositeGroups) as CompositeFilterType[]).flatMap((type) =>
    compositeGroups[type]
      .filter((group) => isCompositeGroupConfigured(type, group.value))
      .map((group) => ({ type, ...group.value })),
  );

  return groups.length > 0
    ? {
        composite_query: { groups },
      }
    : {};
};
